'use client';

import { useEffect, useRef } from 'react';

import { useSearchParams } from 'next/navigation';

import { PasswordRefresh } from '@/components/auth/PasswordRefresh';
import { PasswordReset } from '@/components/auth/PasswordReset';
import { usePopup } from '@/hooks/use-popup';
import { WithLang } from '@/types/general';

import { AuthPopupKeys } from './auth.types';
import { Login } from './Login';
import { Signup } from './Signup';

interface Props {
    onClose: () => void;
    isOpened: boolean;
}

export const Auth = (props: WithLang<Props>) => {
    const { LOGIN, SIGNUP, REFRESH, RESET } = AuthPopupKeys;
    const refAuthContainer = useRef<HTMLDivElement>(null);
    const refAuthComponent = useRef<HTMLDivElement>(null);

    const params = useSearchParams();
    const token = params.get('token') || '';

    const { isVisible, setIsVisible, openPopup } = usePopup({
        [LOGIN]: false,
        [SIGNUP]: false,
        [RESET]: false,
        [REFRESH]: false,
    });

    const closeAuthByMissClick = (e: any) => {
        const target = e.target as Node;
        if (refAuthContainer && !refAuthContainer.current?.contains(target)) {
            props.onClose();
        }
    };

    const updatePopupState = (key: keyof typeof isVisible) => {
        const updatedState: typeof isVisible = {
            [LOGIN]: false,
            [SIGNUP]: false,
            [REFRESH]: false,
            [RESET]: false,
        };
        updatedState[key] = true;
        setIsVisible(updatedState);
    };

    useEffect(() => {
        if (props.isOpened && token) {
            openPopup(RESET);
            return;
        }
        if (props.isOpened && !token) {
            openPopup(LOGIN);
            return;
        }
    }, [props.isOpened]);

    return (
        <div
            ref={refAuthComponent}
            onClick={closeAuthByMissClick}
            className="absolute bottom-0 left-0 right-0 z-[3] h-screen bg-black bg-opacity-40 transition-opacity duration-100"
            style={{ top: refAuthComponent.current?.parentElement?.clientHeight || 90 }}
        >
            <div ref={refAuthContainer} className="auth-container">
                {isVisible.login && (
                    <Login
                        lang={props.lang}
                        isVisible={isVisible[LOGIN]}
                        onRedirect={(key) => updatePopupState(key)}
                        onClose={props.onClose}
                    />
                )}
                {isVisible.signup && (
                    <Signup
                        lang={props.lang}
                        isVisible={isVisible[SIGNUP]}
                        onRedirect={(key) => updatePopupState(key)}
                        onClose={props.onClose}
                    />
                )}
                {isVisible.refresh && (
                    <PasswordRefresh
                        lang={props.lang}
                        isVisible={isVisible[REFRESH]}
                        onRedirect={(key) => updatePopupState(key)}
                        onClose={props.onClose}
                    />
                )}
                {isVisible.reset && (
                    <PasswordReset
                        token={token}
                        lang={props.lang}
                        isVisible={isVisible[RESET]}
                        onRedirect={(key) => updatePopupState(key)}
                        onClose={props.onClose}
                    />
                )}
            </div>
        </div>
    );
};
